import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API } from "@/services/API";
import { validateTrailerType } from "@/utils/validation";
import { sortBy } from "lodash";
import { lockedRateHistory } from "@/utils/lockedData";

const initialState = {
  data: {
    rates: [],
    forecast: [],
    companyLoads: [],
  },
  loading: false,
  error: null,
};

export const getRateHistory = createAsyncThunk(
  "search/get-rate-history",
  async ({ trailerType, pickupZip, deliveryZip }, { rejectWithValue, getState }) => {
    const isLocked = !(getState().user.data?.featuresUnlocked ?? false);

    if (isLocked) {
      return { rates: lockedRateHistory, forecast: [], companyLoads: [] };
    }

    if (!trailerType || !pickupZip || !deliveryZip || !validateTrailerType(trailerType) || isNaN(pickupZip) || isNaN(deliveryZip)) {
      return rejectWithValue("Invalid rate history parameters provided");
    }

    return API.get("/analytics/zip2zip-monthly-load-rate", {
      params: {
        departure: pickupZip,
        "drop-off": deliveryZip,
        trailer: trailerType,
      },
    })
      .then((res) => {
        return {
          rates: sortBy(res.rates || [], ["year", "month"]),
          forecast: sortBy(res.forecast || [], ["year", "month"]),
          companyLoads: res.companyLoads || [],
        };
      })
      .catch((err) => {
        console.error("Error fetching load volume data", err);
        return rejectWithValue(err);
      });
  },
);

const rateHistorySlice = createSlice({
  name: "rateHistory",
  initialState,
  reducers: {
    clearRateHistoryState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRateHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRateHistory.fulfilled, (state, action) => ({
        data: {
          rates: action.payload.rates,
          forecast: action.payload.forecast,
          companyLoads: action.payload.companyLoads,
        },
        loading: false,
        error: null,
      }))
      .addCase(getRateHistory.rejected, () => ({
        data: {
          rates: [],
          forecast: [],
          companyLoads: [],
        },
        loading: false,
        error: "Error getting load volume",
      }));
  },
});

export const { clearRateHistoryState } = rateHistorySlice.actions;
export default rateHistorySlice.reducer;
